import IconButton from "@mui/material/IconButton";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import TransactionHeader from "@app.automotus.io/components/TransactionHeader";
import TransactionDetail from "@app.automotus.io/components/TransactionDetail";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useSearchParams } from "react-router-dom";
import React, { useEffect } from "react";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import {
  GET_TRANSACTION_DETAIL,
  GetTransactionDetailData,
  GetTransactionDetailVars,
  VOID_TRIP_CHARGE,
  VoidTripChargeData,
  VoidTripChargeVars,
} from "common/graphql";
import { PayeeTableDisplayMode } from "@app.automotus.io/components/PayeeTables/PayeeTableToolbar";
import { CurbPassTransactionsTable } from "@app.automotus.io/components/PayeeTables/CurbPassTransactionsTable";
import { useFeatureFlags, useUserProfile } from "@app.automotus.io/components/hooks";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { VoidNoticeDialog } from "@curbsuite/components/VoidNoticeDialog";
import { FEATURE_FLAGS } from "@curbsuite/constants";
import { getAccountFeatures } from "../../utils/userProfile";

export const Dashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactionId, setTransactionId] = React.useState<string>("");
  // The id of the transaction the void dialog was opened for, snapshotted at click time.
  // transactionId can change out from under an open dialog (e.g. browser back/forward
  // navigation updates the transactionId search param), and closing over the live
  // transactionId here would silently retarget the void action at whatever transaction
  // happens to be current when the operator hits submit, rather than the one they were
  // shown a confirmation for.
  const [voidTargetTransactionId, setVoidTargetTransactionId] = React.useState<string | null>(null);
  // Let go of the void target the moment the displayed transaction changes out from under
  // it — whether the dialog is still mounted (transactionId swaps to a different
  // transaction) or it unmounted entirely (transactionId goes back to "" on the table
  // view). Without this, a stale target survives a round trip through the table and can
  // silently reopen the dialog over an unrelated, later-selected transaction.
  useEffect(() => {
    setVoidTargetTransactionId(null);
  }, [transactionId]);
  const { userProfile } = useUserProfile();
  const { isBusStation, isAirportTripCharges } = getAccountFeatures(userProfile);
  const { isEnabled } = useFeatureFlags();
  const { publishSnackbarMessage } = useSnackPackContext();

  const { data: transactionData } = useQuery<GetTransactionDetailData, GetTransactionDetailVars>(
    GET_TRANSACTION_DETAIL,
    {
      variables: { transactionId },
      skip: !transactionId,
    },
  );
  const [voidTripCharge, { loading: voidSubmitting }] = useMutation<VoidTripChargeData, VoidTripChargeVars>(
    VOID_TRIP_CHARGE,
  );

  // Re-derive from both ids on every render (rather than trusting voidTargetTransactionId
  // alone) so a transactionId change closes the dialog immediately, in the same render —
  // the clearing effect above only catches up a render later, leaving a window where the
  // dialog would otherwise still show as open for the previous transaction.
  const activeVoidTargetTransactionId = voidTargetTransactionId === transactionId ? voidTargetTransactionId : null;

  const transaction = transactionData?.transaction;
  const showVoidButton = Boolean(
    isAirportTripCharges &&
      transaction?.transactionType === "trip_charge" &&
      transaction?.status !== "canceled" &&
      !transaction?.settlementTransactionId,
  );

  const handleVoidSubmit = async (
    targetTransactionId: string,
    { reason, voidedAt }: { reason: string; voidedAt: Date },
  ) => {
    try {
      await voidTripCharge({
        variables: { id: targetTransactionId, reason, voidedAt },
        // void_trip_charge is an action, so its response can't be normalized into the
        // financial_transaction cache entity by id — force a refetch of both the detail
        // view's own query and the transactions table (a different type entirely,
        // financial_transaction_log_no_balance, so it never picks up the void on its own).
        refetchQueries: [
          { query: GET_TRANSACTION_DETAIL, variables: { transactionId: targetTransactionId } },
          "GetCurbsuiteTransactions",
        ],
      });
      publishSnackbarMessage({ severity: "success", message: "Trip charge voided" });
      return true;
    } catch (err) {
      console.error("Failed to void trip charge", err);
      const message = err instanceof ApolloError ? err.graphQLErrors[0]?.message ?? err.message : undefined;
      publishSnackbarMessage({
        severity: "error",
        message: message || "Failed to void trip charge",
      });
      return false;
    }
  };

  const { digitalWalletTransactionsEnabled, parkTransactionsEnabled } = getAccountFeatures(userProfile);
  const defaultTab = digitalWalletTransactionsEnabled
    ? "digital_wallet_transactions"
    : parkTransactionsEnabled
    ? "park_transactions"
    : "digital_wallet_transactions";
  const tab = searchParams.get("tab") || defaultTab;

  useEffect(() => {
    if (searchParams?.get("transactionId")) {
      setTransactionId(searchParams?.get("transactionId") as string);
    }
  }, [searchParams]);
  const tableDisplayMode: PayeeTableDisplayMode = (searchParams.get("displayMode") as PayeeTableDisplayMode) || "month";
  const setTableDisplayMode = (newDisplayMode: PayeeTableDisplayMode) => {
    searchParams.set("displayMode", newDisplayMode);
    setSearchParams(searchParams, { replace: true });
  };

  const handleClickBack = () => {
    if (transactionId) {
      searchParams.delete("transactionId");
      setSearchParams(searchParams);
      setTransactionId("");
      return;
    }

    if (tableDisplayMode === "day") {
      setTableDisplayMode("month");
      return;
    }
  };

  return (
    <>
      <Stack spacing={3} width="100%" sx={{ height: "100%", flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <ContentTitle
            displayMode={transactionId ? "transaction" : tableDisplayMode}
            onClickBack={handleClickBack}
            showBackButton={Boolean(transactionId) || tableDisplayMode === "day"}
          />
          {showVoidButton && (
            <Button
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
              onClick={() => setVoidTargetTransactionId(transactionId)}
            >
              Void Trip Charge
            </Button>
          )}
        </Box>
        {transactionId ? (
          <Stack spacing={3}>
            <TransactionHeader transactionId={transactionId} />
            <TransactionDetail transactionId={transactionId} />
            <VoidNoticeDialog
              open={Boolean(activeVoidTargetTransactionId)}
              onClose={() => setVoidTargetTransactionId(null)}
              onSubmit={async (values) => {
                if (!activeVoidTargetTransactionId) {
                  return;
                }
                const succeeded = await handleVoidSubmit(activeVoidTargetTransactionId, values);
                if (succeeded) {
                  // Only clear if nothing has superseded the target this submission was for —
                  // an operator can navigate to another transaction and open its dialog before
                  // this one's mutation resolves, and an unconditional clear here would close
                  // that unrelated, still-in-progress dialog out from under them.
                  setVoidTargetTransactionId((currentTarget) =>
                    currentTarget === activeVoidTargetTransactionId ? null : currentTarget,
                  );
                }
              }}
              entityLabel="Trip Charge"
              isSubmitting={voidSubmitting}
            />
          </Stack>
        ) : (
          <Paper sx={{ width: "100%", flexGrow: 1, display: "flex", flexDirection: "column" }}>
            {/*
              key matches CurbPassTransactionsTable's own tableName scoping (variant + isBusStation)
              and forces a full remount on tab switch — DataGridPro's live columnVisibilityModel and
              DataTableContainer's once-only initialState snapshot don't reset on their own when
              only props change, so a column hidden on one tab would otherwise stay hidden after
              switching to a tab where it should default to visible.
            */}
            <CurbPassTransactionsTable
              key={`${tab === "park_transactions" ? "park" : "wallet"}-${isBusStation}`}
              variant={tab === "park_transactions" ? "park" : "wallet"}
              isBusStation={isBusStation}
              isAirportTripCharges={isAirportTripCharges}
              showsServiceFee={isEnabled(FEATURE_FLAGS.DISPLAY_SERVICE_FEE_COLUMN)}
            />
          </Paper>
        )}
      </Stack>
    </>
  );
};

const ContentTitle: React.FC<ContentTitleProps> = ({ displayMode, onClickBack, showBackButton }) => {
  const title =
    displayMode === "month" ? "Monthly Transactions" : displayMode === "day" ? "Dated Transactions" : "Transaction";
  const description =
    displayMode === "month"
      ? "Recent transactions can take up to 24 hours to process"
      : displayMode === "day"
      ? "Transactions processed for the selected date"
      : "A detailed description of a transaction";

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", alignItems: "center", columnGap: 1 }}>
      <IconButton onClick={onClickBack} sx={{ visibility: showBackButton ? "visible" : "hidden", gridRow: "1 / 3" }}>
        <ArrowBackIosIcon />
      </IconButton>
      <Typography variant={"h4"} fontWeight={600} sx={{ gridColumn: 2 }}>
        {title}
      </Typography>
      <Typography variant={"subtitle1"} color={"text.disabled"} fontSize={"0.75rem"} sx={{ gridColumn: 2 }}>
        {description}
      </Typography>
    </Box>
  );
};

interface ContentTitleProps {
  displayMode: "month" | "day" | "transaction";
  onClickBack: () => void;
  showBackButton: boolean;
}
